import { ReportBI, TaskState } from '@wix/bex-core';

import { NestedCollectionState } from './NestedCollectionState';
import { TableVirtualState } from '../TableVirtualState';
import { NestedTableNodeState } from './NestedTableNodeState';
import { action } from 'mobx';
import { NestedTableState } from './NestedTableState';

export interface NestedTableNestedModeStateBaseParams {}

export interface NestedTableNestedModeStateParams<C extends string>
  extends NestedTableNestedModeStateBaseParams {
  wrapper: NestedTableState<C>;
  reportBi: ReportBI;
}

export class NestedTableNestedModeState<C extends string = string> {
  readonly wrapper;
  readonly container;
  readonly reportBi: ReportBI;
  readonly nestedCollection: NestedCollectionState;
  readonly virtual;

  readonly initTask = new TaskState();

  readonly root: NestedTableNodeState<C, any, any>;

  constructor(params: NestedTableNestedModeStateParams<C>) {
    const { wrapper } = params;
    this.wrapper = wrapper;
    this.container = wrapper.container;

    const rootLevelDescriptor = this.wrapper.levels[0];

    if (rootLevelDescriptor == null) {
      throw new Error('NestedTableState must have at least one level');
    }

    this.nestedCollection = new NestedCollectionState({
      container: this.container,
      levels: wrapper.levels,
      query: wrapper.query,
      nodeCollection: wrapper.rootNodeCollection,
    });

    this.root = new NestedTableNodeState({
      nestedTable: this,
      node: this.nestedCollection.root,
      levelDescriptor: rootLevelDescriptor,
      parentNode: null,
      parent: null,
    });

    this.reportBi = params.reportBi;

    this.virtual = new TableVirtualState({
      table: this,
    });
  }

  get multi() {
    return this.toolbar.multi;
  }

  get tableState() {
    return this.wrapper.tableState;
  }

  get toolbar() {
    return this.wrapper.toolbar;
  }

  get total() {
    return this.nestedCollection.total;
  }

  get originQuery() {
    return this.toolbar.collection.originQuery;
  }

  get keyedItems() {
    return this.root.keyedItems;
  }

  get _keyedItemsMap() {
    return this.root._keyedItemsMap;
  }

  getKeyedItem = (key: string) => this.root.getKeyedItem(key);

  getKeyedItemByIndex = (ind: number) => this.root.getKeyedItemByIndex(ind);

  get expandAll() {
    return this.nestedCollection.expandAll;
  }

  get isFetchAllAborted() {
    return this.nestedCollection.isFetchAllAborted;
  }

  get showEmptyState() {
    return this.toolbar.multi.showEmptyState;
  }

  get showErrorState() {
    const { initTask } = this;
    return initTask.status.isError;
  }

  get showLoadingState() {
    return this.initTask.status.isLoading;
  }

  retryErrorState = action(() => {
    const { initTask } = this;

    if (initTask.status.isError) {
      initTask.runOnce();
    }
  });

  get errorStatus() {
    const { initTask } = this;
    return initTask.errorStatus;
  }

  get query() {
    return this.wrapper.query;
  }

  get levels() {
    return this.wrapper.levels;
  }

  init() {
    const { initTask, wrapper, root } = this;

    const disposers = [
      wrapper.init(),
      root.init({ skipCollectionInit: true }), // collection is initialized by toolbar state
    ];

    initTask.runOnce(async () => {
      wrapper.initTask.runOnce();
      root.initTask.runOnce();

      await root.initTask.status.promise;
      await wrapper.initTask.status.promise;
    });

    return () => {
      disposers.forEach((d) => d());
    };
  }
}
